import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, TrendingUp, Building2, Users, Wrench, Package, Save, History, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ProjectionInputs {
  name: string;
  rent: number;
  squareFootage: number;
  taxesAndCam: number;
  buildoutCost: number;
  equipmentCost: number;
  staffing: {
    therapists: number;
    frontDesk: number;
    techs: number;
  };
  avgVisitsPerDay: number;
  avgPaymentPerVisit: number;
  otherExpenses: number;
}

const LocationProjection: React.FC = () => {
  const [inputs, setInputs] = useState<ProjectionInputs>({
    name: '',
    rent: 5000,
    squareFootage: 3000,
    taxesAndCam: 1000,
    buildoutCost: 150000,
    equipmentCost: 75000,
    staffing: {
      therapists: 2,
      frontDesk: 1,
      techs: 2
    },
    avgVisitsPerDay: 30,
    avgPaymentPerVisit: 85,
    otherExpenses: 2000
  });
  const [savedProjections, setSavedProjections] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSavedProjections();
  }, []);

  const loadSavedProjections = async () => {
    try {
      const { data, error } = await supabase
        .from('location_projections')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedProjections(data || []);
    } catch (err) {
      console.error('Error loading projections:', err);
      setError('Failed to load saved projections');
    }
  };

  const handleSaveProjection = async () => {
    if (!inputs.name) {
      setError('Please enter a name for this projection');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('location_projections')
        .insert({
          name: inputs.name,
          rent: inputs.rent,
          square_footage: inputs.squareFootage,
          taxes_and_cam: inputs.taxesAndCam,
          buildout_cost: inputs.buildoutCost,
          equipment_cost: inputs.equipmentCost,
          therapists: inputs.staffing.therapists,
          front_desk: inputs.staffing.frontDesk,
          techs: inputs.staffing.techs,
          avg_visits_per_day: inputs.avgVisitsPerDay,
          avg_payment_per_visit: inputs.avgPaymentPerVisit,
          other_expenses: inputs.otherExpenses
        });

      if (error) throw error;
      await loadSavedProjections();
      setError(null);
    } catch (err) {
      console.error('Error saving projection:', err);
      setError('Failed to save projection');
    } finally {
      setLoading(false);
    }
  };

  const loadProjection = (projection: any) => {
    setInputs({
      name: projection.name,
      rent: projection.rent,
      squareFootage: projection.square_footage,
      taxesAndCam: projection.taxes_and_cam,
      buildoutCost: projection.buildout_cost,
      equipmentCost: projection.equipment_cost,
      staffing: {
        therapists: projection.therapists,
        frontDesk: projection.front_desk,
        techs: projection.techs
      },
      avgVisitsPerDay: projection.avg_visits_per_day,
      avgPaymentPerVisit: projection.avg_payment_per_visit,
      otherExpenses: projection.other_expenses
    });
    setShowHistory(false);
  };

  // Calculate monthly expenses
  const calculateMonthlyExpenses = () => {
    const staffingCosts = {
      therapist: 6000,
      frontDesk: 2800,
      tech: 2000
    };

    const monthlyStaffing = 
      (inputs.staffing.therapists * staffingCosts.therapist) +
      (inputs.staffing.frontDesk * staffingCosts.frontDesk) +
      (inputs.staffing.techs * staffingCosts.tech);

    const monthlyExpenses = 
      inputs.rent +
      inputs.taxesAndCam +
      monthlyStaffing +
      inputs.otherExpenses;

    return {
      staffing: monthlyStaffing,
      total: monthlyExpenses
    };
  };

  // Calculate loan payments
  const calculateLoanPayments = () => {
    const totalLoan = inputs.buildoutCost + inputs.equipmentCost;
    const annualRate = 0.06; // 6% interest
    const years = 7;
    const monthlyRate = annualRate / 12;
    const numPayments = years * 12;

    const monthlyPayment = 
      (totalLoan * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
      (Math.pow(1 + monthlyRate, numPayments) - 1);

    return monthlyPayment;
  };

  // Calculate breakeven analysis
  const calculateBreakeven = () => {
    const expenses = calculateMonthlyExpenses();
    const loanPayment = calculateLoanPayments();
    const totalMonthlyExpenses = expenses.total + loanPayment;
    
    const monthlyRevenue = inputs.avgVisitsPerDay * 22 * inputs.avgPaymentPerVisit;
    const profitLoss = monthlyRevenue - totalMonthlyExpenses;
    
    const breakevenVisits = Math.ceil(
      (totalMonthlyExpenses) / (inputs.avgPaymentPerVisit * 22)
    );

    return {
      monthlyExpenses: totalMonthlyExpenses,
      monthlyRevenue,
      profitLoss,
      breakevenVisits,
      loanPayment
    };
  };

  const results = calculateBreakeven();

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Location Projection Calculator</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center"
          >
            <History className="h-5 w-5 mr-2" />
            History
          </button>
          <button
            onClick={handleSaveProjection}
            disabled={loading}
            className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Save className="h-5 w-5 mr-2" />
            Save Projection
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Location Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projection Name*
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={inputs.name}
                onChange={(e) => setInputs({ ...inputs, name: e.target.value })}
                placeholder="e.g., New Downtown Location"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Rent
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  <DollarSign className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={inputs.rent}
                  onChange={(e) => setInputs({ ...inputs, rent: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Square Footage
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  <Building2 className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={inputs.squareFootage}
                  onChange={(e) => setInputs({ ...inputs, squareFootage: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Taxes & CAM
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  <DollarSign className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={inputs.taxesAndCam}
                  onChange={(e) => setInputs({ ...inputs, taxesAndCam: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buildout Cost
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  <Wrench className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={inputs.buildoutCost}
                  onChange={(e) => setInputs({ ...inputs, buildoutCost: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Equipment Cost
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  <Package className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={inputs.equipmentCost}
                  onChange={(e) => setInputs({ ...inputs, equipmentCost: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Other Monthly Expenses
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  <DollarSign className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={inputs.otherExpenses}
                  onChange={(e) => setInputs({ ...inputs, otherExpenses: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Staffing</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Therapists
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.staffing.therapists}
                    onChange={(e) => setInputs({
                      ...inputs,
                      staffing: { ...inputs.staffing, therapists: Number(e.target.value) }
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Front Desk
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.staffing.frontDesk}
                    onChange={(e) => setInputs({
                      ...inputs,
                      staffing: { ...inputs.staffing, frontDesk: Number(e.target.value) }
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Techs
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.staffing.techs}
                    onChange={(e) => setInputs({
                      ...inputs,
                      staffing: { ...inputs.staffing, techs: Number(e.target.value) }
                    })}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Revenue Projections</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Avg Visits/Day
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.avgVisitsPerDay}
                    onChange={(e) => setInputs({ ...inputs, avgVisitsPerDay: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Avg Payment/Visit
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                      <DollarSign className="h-5 w-5" />
                    </span>
                    <input
                      type="number"
                      className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={inputs.avgPaymentPerVisit}
                      onChange={(e) => setInputs({ ...inputs, avgPaymentPerVisit: Number(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Monthly Projections</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800">Revenue</h3>
                <p className="text-2xl font-bold text-blue-600">
                  ${results.monthlyRevenue.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-red-800">Expenses</h3>
                <p className="text-2xl font-bold text-red-600">
                  ${results.monthlyExpenses.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-800">Loan Payment</h3>
                <p className="text-2xl font-bold text-gray-600">
                  ${results.loanPayment.toLocaleString()}
                </p>
              </div>
              
              <div className={`p-4 rounded-lg ${
                results.profitLoss >= 0 ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <h3 className={`text-sm font-medium ${
                  results.profitLoss >= 0 ? 'text-green-800' : 'text-red-800'
                }`}>
                  Profit/Loss
                </h3>
                <p className={`text-2xl font-bold ${
                  results.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  ${Math.abs(results.profitLoss).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Breakeven Analysis</h2>
            
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-orange-800">Breakeven Visits/Day</h3>
                    <p className="text-2xl font-bold text-orange-600">{results.breakevenVisits}</p>
                  </div>
                  <Calculator className="h-8 w-8 text-orange-400" />
                </div>
                <p className="text-sm text-orange-700 mt-2">
                  You need {results.breakevenVisits} visits per day to break even
                </p>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-indigo-800">Current Projection</h3>
                    <p className="text-2xl font-bold text-indigo-600">{inputs.avgVisitsPerDay}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-indigo-400" />
                </div>
                <p className="text-sm text-indigo-700 mt-2">
                  Your projected visits per day
                </p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-purple-800">Staff Capacity</h3>
                    <p className="text-2xl font-bold text-purple-600">
                      {inputs.staffing.therapists * 12}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-purple-400" />
                </div>
                <p className="text-sm text-purple-700 mt-2">
                  Maximum visits per day with current staff
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Saved Projections</h2>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Monthly Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Breakeven Visits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {savedProjections.map((projection) => (
                    <tr key={projection.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">{projection.name}</td>
                      <td className="px-6 py-4">
                        {new Date(projection.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        ${(projection.rent + projection.taxes_and_cam + projection.other_expenses).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {Math.ceil(
                          (projection.rent + projection.taxes_and_cam + projection.other_expenses) /
                          (projection.avg_payment_per_visit * 22)
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => loadProjection(projection)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Load
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationProjection;